// The hub cards aboard the in-game ☸ OPTIONS menu (ui-map-interface.md §13,
// R1731): an adapter of the hub `PanelContext` for the game page.
//
// The ALERTS section carries the hub ALERTS card and the SOCIAL section the
// hub BLOCKED card, through their public factories (`html`, `act`, `wire`,
// `open`) and nothing else, so a restyle of a card in the hub reaches the
// game with no change here. PLAY reads and writes the standing orders of
// the account (R1256) through the same typed settings cell as the hub
// WEEKEND row.
//
// The game page builds the adapter only where a meta server stands behind
// it; a direct page has no account, and the menu then says so in one line.
// The cards are built once `/api/me` has answered, because a hub card may
// read the account; until then the two sections show a wait line.

import { HubData } from '../hub/hubstate';
import type { PanelContext } from '../hub/panel';
import { hubHref } from '../hublink';
import { createAlertsCard, type AlertsCard, type BuzzStore } from '../hub/panels/alerts';
import type { FeatureFlag } from '../../gen/featureflags';
import { BLOCKS_CELL, createBlocksCard, type BlocksCard } from '../hub/panels/blocks';
import { SETTINGS_TYPED_CELL, storedSettingsCell } from '../hub/panels/hours';
import { messageOf, putStandingOrders, type BlockList, type Me } from '../../net/api';
import type { PageId } from './sections';

/** What the game page hands the adapter. */
export interface GameHostDeps {
  /** Reads the account once. A failure leaves the cards unbuilt. */
  getMe(): Promise<Me>;
  /** Draws the menu again; the overlay diffs the markup. */
  refresh(): void;
  /** True while the menu is the desktop popup. */
  wide(): boolean;
  /** Leaves the world for one address of the hub (`hubHref`), the way
   *  MENU leaves. */
  go(href: string): void;
  /** The BUZZ switches, kept in the overlay's own settings, so the game's
   *  buzzer and a later switch of the menu read one blob (R1613). */
  buzz: BuzzStore;
  /** The latched flags of the world: the ALERTS card shows the row of a
   *  push class behind a flag while it holds (R2637). */
  hasFlag?: (flag: FeatureFlag) => boolean;
}

/** What the PLAY section shows of the standing orders. */
export interface StandingView {
  /** The switch as it stands, or undefined while the first read travels. */
  on?: boolean;
  busy: boolean;
  /** The refusal of the last write, or ''. */
  err: string;
}

/** What the overlay reads of the adapter. A test hands in its own. */
export interface OptionsHost {
  /** The account, once it answered. */
  me(): Me | null;
  /** A page of the menu opened. */
  open(page: PageId): void;
  /** The ALERTS and BLOCKED cards, or null while they wait. */
  alertsHtml(): string | null;
  chatHtml(): string | null;
  /** The length of the block list, where the cache has read it. */
  blockedCount(): number | undefined;
  /** One press on a card; false for an action no card owns. */
  act(action: string, el: HTMLElement): boolean;
  /** Wires the inputs of a card after a rebuild of the pane. */
  wire(root: HTMLElement): void;
  /** The standing orders, and one press on their switch. */
  standing(): StandingView;
  toggleStanding(): void;
}

export class GameHost implements OptionsHost {
  /** The hub cache the cards read through. */
  readonly data: HubData;
  private account: Me | null = null;
  private readonly ctx: PanelContext;
  private alerts: AlertsCard | null = null;
  private blocks: BlocksCard | null = null;
  /** The standing orders as the last press left them (R1256). */
  private picked: boolean | undefined;
  private busy = false;
  private err = '';

  constructor(private readonly deps: GameHostDeps) {
    this.data = new HubData(() => deps.refresh());
    this.ctx = {
      me: () => {
        if (!this.account) throw new Error('the account has not answered yet');
        return this.account;
      },
      setMe: (me) => { this.account = me; },
      data: this.data,
      now: () => Date.now() / 1000,
      refresh: () => deps.refresh(),
      // The game page joins no world from a card.
      join: () => {},
      go: (tab, at) => deps.go(hubHref(tab, at)),
      wide: () => deps.wide(),
      // The menu of the game plays no menu cue.
      play: () => {},
    };
    void deps.getMe().then((me) => {
      this.account = me;
      this.alerts = createAlertsCard(this.ctx, undefined, undefined, deps.buzz, false, deps.hasFlag);
      this.blocks = createBlocksCard(this.ctx);
      deps.refresh();
    }, () => { /* no account: the sections keep their wait line */ });
  }

  /** The account, once it answered. */
  me(): Me | null {
    return this.account;
  }

  /** A page of the menu opened: the cards start their reads. The block
   *  list is read again, because a BLOCK in the chat changed it. */
  open(page: PageId): void {
    if (page === 'alerts') this.alerts?.open();
    if (page === 'social') this.data.drop(BLOCKS_CELL);
  }

  /** The ALERTS card, or null until the account answered. */
  alertsHtml(): string | null {
    return this.alerts?.html() ?? null;
  }

  /** The BLOCKED card, or null until the account answered. */
  chatHtml(): string | null {
    return this.blocks?.html() ?? null;
  }

  /** How many accounts the block list holds, where the cache has read it.
   *  It starts no read. */
  blockedCount(): number | undefined {
    const read = this.data.peek<BlockList>(BLOCKS_CELL);
    return read.state === 'ready' ? read.value?.blocks.length : undefined;
  }

  /** One press on a card. Answers false for an action no card owns. */
  act(action: string, el: HTMLElement): boolean {
    return (this.alerts?.act(action, el) ?? false) || (this.blocks?.act(action, el) ?? false);
  }

  /** Wires the inputs of a card after the pane was rebuilt. */
  wire(root: HTMLElement): void {
    this.alerts?.wire(root);
  }

  /** The standing orders of the account. A server that predates the field
   *  reads ON, the default of migration 048. */
  standing(): StandingView {
    let on = this.picked;
    if (on === undefined) {
      const read = storedSettingsCell(this.ctx);
      if (read.state === 'ready') on = read.value?.standingOrders ?? true;
    }
    return { on, busy: this.busy, err: this.err };
  }

  /** One press on the standing-orders switch: it writes at once, with its
   *  own PUT, and the row shows the value the server answered. */
  toggleStanding(): void {
    const on = this.standing().on;
    if (on === undefined || this.busy) return;
    this.busy = true;
    this.err = '';
    this.deps.refresh();
    void putStandingOrders(!on).then(
      (r) => { this.picked = r.standingOrders; this.data.drop(SETTINGS_TYPED_CELL); },
      (e: unknown) => { this.err = messageOf(e); },
    ).finally(() => { this.busy = false; this.deps.refresh(); });
  }
}
